import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { logisticsApi } from '../../network/apis/logistics';
import { User, Settings, Truck } from '../../assets/icons';
import '../../assets/css/logistics/profile.css';

const LogisticsProfile = () => {
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [company, setCompany] = useState(null);
    const [formData, setFormData] = useState({
        company_name: '',
        contact_email: '',
        contact_phone: '',
        description: '',
        address: '',
        city: '',
        state: '',
        coverage_areas: [],
        service_types: [],
        bank_name: '',
        account_number: '',
        account_name: ''
    });

    const nigerianStates = [
        'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 
        'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT',
        'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi',
        'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo',
        'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
    ];

    const serviceTypes = [
        'Same Day Delivery',
        'Express Delivery',
        'Standard Delivery',
        'Bulk Shipping',
        'Cold Chain',
        'Fragile Items',
        'Documents'
    ];

    const fetchCompany = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await logisticsApi.getMyCompany();
            const data = response.data?.data || response.data;
            setCompany(data);
            setFormData({
                company_name: data.company_name || '',
                contact_email: data.contact_email || '',
                contact_phone: data.contact_phone || '',
                description: data.description || '',
                address: data.address || '',
                city: data.city || '',
                state: data.state || '',
                coverage_areas: data.coverage_areas || [],
                service_types: data.service_types || [],
                bank_name: data.bank_name || '',
                account_number: data.account_number || '',
                account_name: data.account_name || ''
            });
        } catch (err) {
            console.error('Failed to fetch company:', err);
            setError(err.response?.data?.message || 'Failed to load company profile');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCompany();
    }, [fetchCompany]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCoverageToggle = (state) => {
        setFormData(prev => ({
            ...prev,
            coverage_areas: prev.coverage_areas.includes(state)
                ? prev.coverage_areas.filter(s => s !== state)
                : [...prev.coverage_areas, state]
        }));
    };

    const handleServiceToggle = (service) => {
        setFormData(prev => ({
            ...prev,
            service_types: prev.service_types.includes(service)
                ? prev.service_types.filter(s => s !== service)
                : [...prev.service_types, service]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError('');
            await logisticsApi.updateMyCompany(formData);
            setSuccess('Profile updated successfully');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update profile');
            setTimeout(() => setError(''), 5000);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="logistics-profile">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="logistics-profile">
            <header className="page-header">
                <h1>Company Profile</h1>
            </header>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="profile-grid">
                <aside className="profile-sidebar">
                    <div className="company-card">
                        <img 
                            src={company?.logo || '/default-company.png'} 
                            alt={company?.company_name}
                            className="company-logo"
                            onError={(e) => e.target.src = '/default-avatar.png'}
                        />
                        <h2>{company?.company_name || 'Your Company'}</h2>
                        <span className={`verification-badge ${company?.is_verified ? 'verified' : 'pending'}`}>
                            {company?.is_verified ? '✓ Verified' : '⏳ Pending Verification'}
                        </span>
                        
                        <div className="company-stats">
                            <div className="stat-item">
                                <div className="value">{company?.total_deliveries || 0}</div>
                                <div className="label">Deliveries</div>
                            </div>
                            <div className="stat-item">
                                <div className="value">★ {company?.rating?.toFixed(1) || '0.0'}</div>
                                <div className="label">Rating</div>
                            </div>
                        </div>
                    </div>

                    <nav className="nav-menu">
                        <Link 
                            to="/logistics/profile" 
                            className={`nav-link ${location.pathname === '/logistics/profile' ? 'active' : ''}`}
                        >
                            <img src={User} alt="" />
                            Company Info
                        </Link>
                        <Link 
                            to="/logistics/rates" 
                            className={`nav-link ${location.pathname === '/logistics/rates' ? 'active' : ''}`}
                        >
                            <img src={Settings} alt="" />
                            Shipping Rates
                        </Link>
                        <Link 
                            to="/logistics/vehicles" 
                            className={`nav-link ${location.pathname === '/logistics/vehicles' ? 'active' : ''}`}
                        >
                            <img src={Truck} alt="" />
                            Vehicles
                        </Link>
                    </nav>
                </aside>

                <main className="profile-main">
                    <form onSubmit={handleSubmit}>
                        <h3 className="section-title">Company Information</h3>
                        
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Company Name *</label>
                                <input
                                    type="text"
                                    name="company_name"
                                    value={formData.company_name}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Contact Email *</label>
                                <input
                                    type="email"
                                    name="contact_email"
                                    value={formData.contact_email}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Contact Phone *</label>
                                <input
                                    type="tel"
                                    name="contact_phone"
                                    value={formData.contact_phone}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>State *</label>
                                <select
                                    name="state"
                                    value={formData.state}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select state...</option>
                                    {nigerianStates.map(state => (
                                        <option key={state} value={state}>{state}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="form-group">
                                <label>City *</label>
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Address</label>
                                <input
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                />
                            </div>
                            
                            <div className="form-group full-width">
                                <label>Description</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows="3"
                                    placeholder="Tell customers about your logistics company..."
                                />
                            </div>
                        </div>

                        <h3 className="section-title" style={{ marginTop: '2rem' }}>Coverage Areas</h3>
                        <p className="help-text" style={{ marginBottom: '1rem' }}>Select the states you service</p>
                        
                        <div className="tags-input">
                            {nigerianStates.map(state => (
                                <span
                                    key={state}
                                    className={`tag ${formData.coverage_areas.includes(state) ? '' : 'inactive'}`}
                                    onClick={() => handleCoverageToggle(state)}
                                    style={{
                                        cursor: 'pointer',
                                        opacity: formData.coverage_areas.includes(state) ? 1 : 0.5,
                                        background: formData.coverage_areas.includes(state) 
                                            ? 'var(--primary-blue-light)' 
                                            : 'var(--gray-100)'
                                    }}
                                >
                                    {state}
                                </span>
                            ))}
                        </div>

                        <h3 className="section-title" style={{ marginTop: '2rem' }}>Service Types</h3>
                        <p className="help-text" style={{ marginBottom: '1rem' }}>Select the services you offer</p>
                        
                        <div className="tags-input">
                            {serviceTypes.map(service => (
                                <span
                                    key={service}
                                    className={`tag ${formData.service_types.includes(service) ? '' : 'inactive'}`}
                                    onClick={() => handleServiceToggle(service)}
                                    style={{
                                        cursor: 'pointer',
                                        opacity: formData.service_types.includes(service) ? 1 : 0.5,
                                        background: formData.service_types.includes(service) 
                                            ? 'var(--primary-blue-light)' 
                                            : 'var(--gray-100)'
                                    }}
                                >
                                    {service}
                                </span>
                            ))}
                        </div>

                        <h3 className="section-title" style={{ marginTop: '2rem' }}>Banking Information</h3>
                        
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Bank Name</label>
                                <input
                                    type="text"
                                    name="bank_name"
                                    value={formData.bank_name}
                                    onChange={handleInputChange}
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Account Number</label>
                                <input
                                    type="text"
                                    name="account_number"
                                    value={formData.account_number}
                                    onChange={handleInputChange}
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Account Name</label>
                                <input
                                    type="text"
                                    name="account_name"
                                    value={formData.account_name}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn btn-secondary" onClick={fetchCompany}>
                                Reset
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </main>
            </div>
        </div>
    );
};

export default LogisticsProfile;
